import { AfterViewInit, Component, Input, OnInit, ViewChildren } from "@angular/core";
import { QuestionRange, QuestionRangeValue } from "src/app/main/model/forms/question-range.model";
import { FormCompletionBaseComponent } from "../form-completion-base.component";

/** Radio scales with at least this many points (e.g. 0–10) use the one-row layout on phones in landscape. */
const LONG_RADIO_SCALE_MIN_OPTIONS = 7;

@Component({
  selector: "app-question-range",
  templateUrl: "./question-range.component.html",
  styleUrls: ["./question-range.component.scss"],
  standalone: false,
})
export class QuestionRangeComponent
  extends FormCompletionBaseComponent
  implements OnInit, AfterViewInit
{
  @ViewChildren("input") inputRefs;

  @Input() public question: QuestionRange;

  public currentValue;
  // The scale slider is index-based so its points follow the questionnaire's display order (which may
  // differ from numeric value). The form control still stores each option's internal value.
  public scaleIndex = 0;

  ngOnInit() {
    this.scaleIndex = Math.max(
      0,
      this.scaleRanges.findIndex((r) => r.value === this.control?.value)
    );
  }

  ngAfterViewInit() {
    for (let i = 0; i < this.inputRefs._results.length; i++) {
      this.inputRefs._results[i].nativeElement.name = this.question.getName();
    }
  }

  public selectOption(value: number): void {
    if (this.service) {
      this.recordExplicitAnswer(this.question, value);
    } else {
      this.currentValue = value;
    }
  }

  // Slider position → the internal value of the option at that display index.
  public onScaleIndexChange(index: number): void {
    this.scaleIndex = index;
    const range = this.scaleRanges[index];
    if (range) {
      this.selectOption(range.value);
    }
  }

  /**
   * Confirms a pointer interaction even when the thumb remains on its initial visual position.
   * @param index Display index reported by the Material slider.
   */
  public confirmScaleIndex(index: number): void {
    this.onScaleIndexChange(index);
  }

  /**
   * Records supported keyboard slider interactions without treating Tab focus as an answer.
   * @param event Keyboard event emitted by the slider thumb.
   */
  public confirmScaleFromKeyboard(event: KeyboardEvent): void {
    if (this.isSliderInteractionKey(event.key)) {
      this.confirmScaleIndex(this.scaleIndex);
    }
  }

  public get scaleMaxIndex(): number {
    return Math.max(0, this.scaleRanges.length - 1);
  }

  // Evenly space the tick labels by display index (not by numeric value).
  public indexPosition(index: number): number {
    const max = this.scaleMaxIndex;
    return max <= 0 ? 0 : (index / max) * 100;
  }

  public displayIndex = (index: number): string => {
    return this.scaleRanges[index]?.description ?? "";
  };

  public get control() {
    return this.service.formGroup.controls[this.question.getName()];
  }

  public get useIcons(): boolean {
    return !!this.question.viewMeta?.useIcons;
  }
  public get useScale(): boolean {
    return !!this.question.viewMeta?.useScale;
  }

  /** Long radio scales (e.g. 0–10) that need the compact one-row layout on phones in landscape. */
  public get isLongRadioScale(): boolean {
    return (
      !this.useScale &&
      !this.useIcons &&
      this.question.questionRangeGroup.ranges.length >= LONG_RADIO_SCALE_MIN_OPTIONS
    );
  }
  public get useGradient(): boolean {
    return !!this.question.viewMeta?.useGradient;
  }
  public get gradientColour1(): string {
    return this.question.viewMeta?.gradientColour1 || "";
  }
  public get gradientColour2(): string {
    return this.question.viewMeta?.gradientColour2 || "";
  }
  public get scaleGradient(): string {
    const c1 =
      this.service.themeService.getThemeColor(
        this.service.themeService.ThemeItemEnum.gradientStart
      ) ||
      this.gradientColour1 ||
      "#e0e0e0";
    const c2 =
      this.service.themeService.getThemeColor(
        this.service.themeService.ThemeItemEnum.gradientEnd
      ) ||
      this.gradientColour2 ||
      "#6c6c6c";
    return `linear-gradient(to right, ${c1}, ${c2})`;
  }

  public get scaleRanges(): QuestionRangeValue[] {
    return this.question.questionRangeGroup.ranges.filter((r) => r.value !== -999);
  }

  private get scaleValues(): number[] {
    return this.scaleRanges.map((r) => r.value);
  }

  public get firstLabel(): string {
    return this.scaleRanges[0]?.description ?? "";
  }

  public get lastLabel(): string {
    const ranges = this.scaleRanges;
    return ranges[ranges.length - 1]?.description ?? "";
  }

  public get sliderMin(): number {
    return Math.min(...this.scaleValues);
  }

  public get sliderMax(): number {
    return Math.max(...this.scaleValues);
  }

  public labelPosition(value: number): number {
    const span = this.sliderMax - this.sliderMin;
    if (span <= 0) {
      return 0;
    }
    return ((value - this.sliderMin) / span) * 100;
  }

  public get hasValue(): boolean {
    const value = this.control?.value;
    return value !== null && value !== undefined && value !== "";
  }

  public descriptionFor = (value: number): string => {
    if (value === null || value === undefined || (value as unknown) === "") {
      return "";
    }
    return (
      this.question.questionRangeGroup.ranges.find((r) => r.value === value)?.description ??
      `${value}`
    );
  };
}
